import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  light: {
    id: 'light',
    label: 'Claro',
    icon: 'sun',
    bg: '#FFFFFF',
    bgSecondary: '#F8FAFC',
    bgTertiary: '#F1F5F9',
    surface: '#FFFFFF',
    surfaceHover: '#F8FAFC',
    cardBg: '#FFFFFF',
    cardBorder: '#E2E8F0',
    text: '#1E293B',
    textSecondary: '#475569',
    textMuted: '#94A3B8',
    primary: '#25255B',
    primaryLight: '#3A71B5',
    accent: '#F05864',
    accentHover: '#D64550',
    navBg: 'rgba(255,255,255,0.9)',
    navText: '#25255B',
    footerBg: '#25255B',
    footerText: '#FFFFFF',
    footerTextMuted: '#CBD5E1',
    heroBg: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 50%, #F0F9FF 100%)',
    sectionAltBg: '#F8FAFC',
    borderColor: '#E2E8F0',
    shadowColor: 'rgba(37,37,91,0.08)',
    shadowHover: 'rgba(37,37,91,0.15)',
    featureIconBg: '#EEF2FF',
    featureIconColor: '#3A71B5',
    statBg: '#FFFFFF',
    badgeBg: '#EEF2FF',
    badgeText: '#3A71B5',
    inputBg: '#FFFFFF',
    inputBorder: '#E2E8F0',
  },
  dark: {
    id: 'dark',
    label: 'Oscuro',
    icon: 'moon',
    bg: '#0F1029',
    bgSecondary: '#161838',
    bgTertiary: '#1A1F45',
    surface: '#1E2252',
    surfaceHover: '#252A5E',
    cardBg: '#1A1F45',
    cardBorder: '#2E3B6B',
    text: '#FFFFFF',
    textSecondary: '#CBD5E1',
    textMuted: '#8892B0',
    primary: '#FFFFFF',
    primaryLight: '#93B5E8',
    accent: '#F05864',
    accentHover: '#FF6B76',
    navBg: 'rgba(15,16,41,0.95)',
    navText: '#FFFFFF',
    footerBg: '#0A0B1E',
    footerText: '#FFFFFF',
    footerTextMuted: '#8892B0',
    heroBg: 'linear-gradient(135deg, #0F1029 0%, #1A1F45 50%, #25255B 100%)',
    sectionAltBg: '#161838',
    borderColor: '#2E3B6B',
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowHover: 'rgba(0,0,0,0.5)',
    featureIconBg: '#25255B',
    featureIconColor: '#93B5E8',
    statBg: '#1A1F45',
    badgeBg: '#25255B',
    badgeText: '#93B5E8',
    inputBg: '#1A1F45',
    inputBorder: '#2E3B6B',
  },
  aurora: {
    id: 'aurora',
    label: 'Aurora',
    icon: 'sparkles',
    bg: '#0C0F1D',
    bgSecondary: '#111428',
    bgTertiary: '#161933',
    surface: 'rgba(30,34,82,0.5)',
    surfaceHover: 'rgba(42,47,100,0.6)',
    cardBg: 'rgba(26,31,69,0.6)',
    cardBorder: 'rgba(58,113,181,0.25)',
    text: '#FFFFFF',
    textSecondary: '#C8D6E5',
    textMuted: '#8892B0',
    primary: '#FFFFFF',
    primaryLight: '#7EB8FF',
    accent: '#F05864',
    accentHover: '#FF6B76',
    navBg: 'rgba(12,15,29,0.85)',
    navText: '#FFFFFF',
    footerBg: '#080A18',
    footerText: '#FFFFFF',
    footerTextMuted: '#8892B0',
    heroBg: 'linear-gradient(135deg, #0C0F1D 0%, #131640 30%, #1A1F55 60%, #0C0F1D 100%)',
    sectionAltBg: '#111428',
    borderColor: 'rgba(58,113,181,0.2)',
    shadowColor: 'rgba(58,113,181,0.1)',
    shadowHover: 'rgba(58,113,181,0.25)',
    featureIconBg: 'rgba(58,113,181,0.15)',
    featureIconColor: '#7EB8FF',
    statBg: 'rgba(26,31,69,0.5)',
    badgeBg: 'rgba(58,113,181,0.2)',
    badgeText: '#7EB8FF',
    inputBg: 'rgba(26,31,69,0.5)',
    inputBorder: 'rgba(58,113,181,0.3)',
    auroraGlow1: 'rgba(58,113,181,0.15)',
    auroraGlow2: 'rgba(126,184,255,0.08)',
    auroraGlow3: 'rgba(240,88,100,0.06)',
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('motivy-theme');
    return saved && THEMES[saved] ? saved : 'dark';
  });

  useEffect(() => {
    localStorage.setItem('motivy-theme', theme);
  }, [theme]);

  const currentTheme = THEMES[theme];

  const cycleTheme = () => {
    const keys = Object.keys(THEMES);
    const idx = keys.indexOf(theme);
    setTheme(keys[(idx + 1) % keys.length]);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, currentTheme, cycleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
